import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, StatusBadge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { SelectField, TextField } from '@/components/forms/Field';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { employeesApi } from '@/services/api/endpoints/staff';
import { usersApi } from '@/services/api/endpoints/identity';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { Employee, EmploymentStatus, EmploymentType } from '@/types/domain';
import { formatDate, humanise, orDash } from '@/utils/format';

const TYPES: EmploymentType[] = ['FULL_TIME', 'PART_TIME', 'CONTRACT', 'VISITING'];

const END_STATUSES: EmploymentStatus[] = ['RESIGNED', 'TERMINATED', 'RETIRED'];

/**
 * Employment records.
 *
 * An employee is attached to an existing user account rather than carrying its
 * own name and e-mail: one person, one identity. That is why this screen offers
 * a user picker instead of name fields, and why ending employment never deletes
 * the row — past timetables and attendance still point at it.
 */
export function EmployeesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('employeeNumber,asc');

  const [statusFilter, setStatusFilter] = useState<EmploymentStatus | ''>('');
  const [formOpen, setFormOpen] = useState(false);
  const [ending, setEnding] = useState<Employee | null>(null);
  const [form, setForm] = useState({
    userId: '',
    employeeNumber: '',
    joiningDate: '',
    employmentType: 'FULL_TIME' as EmploymentType,
    designation: '',
  });
  const [endForm, setEndForm] = useState({
    status: 'RESIGNED' as EmploymentStatus,
    leavingDate: '',
    reason: '',
  });

  const params = { ...table.params, ...(statusFilter !== '' ? { status: statusFilter } : {}) };

  const query = useQuery({
    queryKey: queryKeys.employees.list(params),
    queryFn: () => employeesApi.list(params),
  });

  /* Only accounts without an employment record can be attached, since the
     backend permits one per user. */
  const users = useQuery({
    queryKey: queryKeys.users.list({ size: 200 }),
    queryFn: () => usersApi.list({ page: 0, size: 200 }),
    enabled: formOpen && can(PERMISSIONS.USER_VIEW),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: queryKeys.employees.all });

  const create = useMutation({
    mutationFn: () =>
      employeesApi.create({
        userId: form.userId,
        employeeNumber: form.employeeNumber === '' ? undefined : form.employeeNumber,
        joiningDate: form.joiningDate,
        employmentType: form.employmentType,
        designation: form.designation === '' ? undefined : form.designation,
      }),
    onSuccess: (employee) => {
      toast.success(`${employee.fullName ?? 'Employee'} added`);
      setFormOpen(false);
      setForm({ ...form, userId: '', employeeNumber: '', designation: '' });
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const changeStatus = useMutation({
    mutationFn: () =>
      employeesApi.changeStatus(ending?.id ?? '', {
        status: endForm.status,
        leavingDate: endForm.leavingDate === '' ? undefined : endForm.leavingDate,
        reason: endForm.reason === '' ? undefined : endForm.reason,
      }),
    onSuccess: () => {
      toast.success('Employment status updated');
      setEnding(null);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<Employee>[] = [
    {
      key: 'number',
      header: 'Number',
      sortKey: 'employeeNumber',
      render: (employee) => <span className="font-mono text-xs text-muted">{employee.employeeNumber}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      render: (employee) => (
        <div>
          <div className="font-medium text-ink">{orDash(employee.fullName)}</div>
          <div className="text-xs text-subtle">{orDash(employee.email)}</div>
        </div>
      ),
    },
    { key: 'designation', header: 'Designation', render: (employee) => orDash(employee.designation) },
    {
      key: 'type',
      header: 'Type',
      render: (employee) => <span className="text-sm">{humanise(employee.employmentType)}</span>,
    },
    {
      key: 'joined',
      header: 'Joined',
      sortKey: 'joiningDate',
      render: (employee) => formatDate(employee.joiningDate),
    },
    {
      key: 'teacher',
      header: 'Teaches',
      render: (employee) => (employee.teacher ? <Badge tone="info">Teacher</Badge> : null),
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'employmentStatus',
      render: (employee) => <StatusBadge status={employee.employmentStatus} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (employee) =>
        can(PERMISSIONS.EMPLOYEE_UPDATE) && employee.employmentStatus !== 'RESIGNED' ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={(event) => {
              event.stopPropagation();
              setEnding(employee);
            }}
          >
            Change status
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <ListPage
        title="Employees"
        description="Everyone the school employs. Teaching staff also hold a teacher record."
        action={
          can(PERMISSIONS.EMPLOYEE_CREATE) ? (
            <Button onClick={() => setFormOpen(true)}>Add employee</Button>
          ) : undefined
        }
        search={{
          value: table.searchInput,
          onChange: table.setSearchInput,
          placeholder: 'Search by number or designation…',
        }}
        filters={
          <FilterSelect
            label="Status"
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              table.setPage(0);
            }}
            options={[
              { value: 'ACTIVE', label: 'Active' },
              { value: 'ON_LEAVE', label: 'On leave' },
              { value: 'SUSPENDED', label: 'Suspended' },
              { value: 'RESIGNED', label: 'Resigned' },
              { value: 'RETIRED', label: 'Retired' },
            ]}
          />
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(employee) => employee.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No employees found"
          emptyDescription="Add an employment record for an existing user account."
        />
      </ListPage>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title="Add employee"
        description="Attaches employment details to an existing user account, so name and e-mail stay in one place."
        footer={
          <>
            <Button variant="ghost" onClick={() => setFormOpen(false)} disabled={create.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => create.mutate()}
              loading={create.isPending}
              disabled={form.userId === '' || form.joiningDate === ''}
            >
              Add employee
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <SelectField
            label="User account"
            required
            value={form.userId}
            onChange={(event) => setForm({ ...form, userId: event.target.value })}
            hint="One employment record per account"
          >
            <option value="">Select a user…</option>
            {users.data?.data.map((user) => (
              <option key={user.id} value={user.id}>
                {user.fullName} · {user.email}
              </option>
            ))}
          </SelectField>

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Employee number"
              value={form.employeeNumber}
              onChange={(event) => setForm({ ...form, employeeNumber: event.target.value })}
              hint="Generated if left blank"
            />
            <TextField
              label="Joining date"
              type="date"
              required
              value={form.joiningDate}
              onChange={(event) => setForm({ ...form, joiningDate: event.target.value })}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              label="Employment type"
              required
              value={form.employmentType}
              onChange={(event) =>
                setForm({ ...form, employmentType: event.target.value as EmploymentType })
              }
            >
              {TYPES.map((type) => (
                <option key={type} value={type}>
                  {humanise(type)}
                </option>
              ))}
            </SelectField>
            <TextField
              label="Designation"
              value={form.designation}
              onChange={(event) => setForm({ ...form, designation: event.target.value })}
              placeholder="e.g. Head of Mathematics"
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={ending !== null}
        onClose={() => setEnding(null)}
        title={`Change ${ending?.fullName ?? 'employee'}’s status`}
        description="Ending employment keeps the record: past timetables, assignments and attendance all point at it."
        footer={
          <>
            <Button variant="ghost" onClick={() => setEnding(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => changeStatus.mutate()}
              loading={changeStatus.isPending}
              disabled={END_STATUSES.includes(endForm.status) && endForm.leavingDate === ''}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <SelectField
            label="Status"
            required
            value={endForm.status}
            onChange={(event) =>
              setEndForm({ ...endForm, status: event.target.value as EmploymentStatus })
            }
          >
            <option value="ACTIVE">Active</option>
            <option value="ON_LEAVE">On leave</option>
            <option value="SUSPENDED">Suspended</option>
            <option value="RESIGNED">Resigned</option>
            <option value="TERMINATED">Terminated</option>
            <option value="RETIRED">Retired</option>
          </SelectField>

          {END_STATUSES.includes(endForm.status) && (
            <TextField
              label="Leaving date"
              type="date"
              required
              value={endForm.leavingDate}
              onChange={(event) => setEndForm({ ...endForm, leavingDate: event.target.value })}
              hint="Required when employment ends"
            />
          )}

          <TextField
            label="Reason"
            value={endForm.reason}
            onChange={(event) => setEndForm({ ...endForm, reason: event.target.value })}
          />
        </div>
      </Modal>
    </>
  );
}
